// utils/qzPrint.js
// Impression “protocole + base64” sans détection, SANS toucher aux chaînes.
// On envoie exactement ce que tu passes dans `etiquettes`.
// Envoi par paquets (10 par défaut) pour gros volumes.

import {
  initSecurity,
  ensureConnectedWithHealth,
  getThermalPrinterConfigFor,
  setPreferredQueues,
} from './qzHelpers';


// --- helpers de parsing brut ---
function getParamRaw(urlStr, key) {
  const qIdx = urlStr.indexOf('?');
  if (qIdx === -1) return null;
  let q = urlStr.slice(qIdx + 1);
  const hIdx = q.indexOf('#');
  if (hIdx !== -1) q = q.slice(0, hIdx);
  const esc = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp('(?:^|&)' + esc + '=([^&]*)');
  const m = re.exec(q);
  return m ? m[1] : null; // valeur brute
}

// ---------- logs
function L(level){ return function(){ var a=[].slice.call(arguments); a.unshift('[QZ] ['+level.toUpperCase()+']'); console.log.apply(console,a); }; }
const INFO  = L('info');
const DEBUG = L('debug');
const ERROR = L('error');

// ---------- helpers min
function _toArray(x){ return Array.isArray(x) ? x : (x == null ? [] : [x]); }
function _stripOuterQuotes(s){ if(typeof s!=='string')return s; if((s.startsWith("'")&&s.endsWith("'"))||(s.startsWith('"')&&s.endsWith('"')))return s.slice(1,-1); return s; }
function _protoFromProtocole(p){
  var v=(p||'').toString().trim().toUpperCase();
  if (v === 'DATAMAX') return 'DPL';
  if (v === 'ZEBRA')   return 'ZPL';
  return 'RAW';
}

// (facultatif) parse depuis une URL si jamais tu continues à passer l’URL brute
function _parseFromUrl(url){
  try{
    var U = new URL(_stripOuterQuotes(url));
    var proto = U.searchParams.get('protocole') || U.searchParams.get('protocol') || '';
    var etiqs = [];
    // autorise etiquette=... multiple
    U.searchParams.forEach(function(v,k){
      if (k.toLowerCase()==='etiquette') etiqs.push(v); // **NE PAS TOUCHER** (pas de decoding supplémentaire ici)
    });
    var one = U.searchParams.get('etiquette');
    if (one && etiqs.length===0) etiqs = [one];
    return { protocole: proto, etiquettes: etiqs };
  } catch(e){
    return null;
  }
}

/**
 * printLabels(
 *   { protocole: 'DATAMAX'|'ZEBRA'|'', etiquettes: string|string[], url?: string },
 *   { security?: {certUrl,signUrl,algo}, logLevel?: 'debug'|'info'|'warn'|'error', chunkSize?: number,
 *     preferredQueues?: { DPL?:string, ZPL?:string, RAW?:string }, printerNameOverride?: string }
 * )
 */
export function printLabels(payload, options){
  var opts = options || {};
  if (opts.security) initSecurity(opts.security);
  if (opts.preferredQueues) setPreferredQueues(opts.preferredQueues);

  var level = (opts.logLevel || 'info').toLowerCase();
  INFO('logLevel =', level);

  // 1) Normaliser sources, mais **sans modifier les chaînes**
  var protocole = (payload && payload.protocole) || '';
  var etiquettes = _toArray(payload && payload.etiquettes);

  if (payload && payload.url){
    var parsed = _parseFromUrl(payload.url);
    if (parsed){
      protocole = parsed.protocole || protocole;
      if (parsed.etiquettes && parsed.etiquettes.length) etiquettes = parsed.etiquettes;
    }
  }

  if (!etiquettes.length){
    throw new Error('Aucune étiquette fournie');
  }

  // Retire juste les guillemets externes éventuels (copier/coller), mais pas d’autres modifs
  etiquettes = etiquettes.map(function(e){ return _stripOuterQuotes(String(e ?? '')); });

  var proto = _protoFromProtocole(protocole);
  INFO('protocole =', protocole, '→ proto =', proto, '| count =', etiquettes.length);

  // 2) Connexion + config (réutilisée via cache)
  return ensureConnectedWithHealth()
    .then(function(){
      return getThermalPrinterConfigFor(proto, {
        jobName: 'Colissimo '+proto,
        printerName: opts.printerNameOverride || null,
        protocoleOriginal: protocole  // Passer le protocole original pour MUNBYN
      });
    })
    .then(function(cfg){
      // 3) Envoi par paquets (10 par défaut, ou opts.chunkSize)
      var chunkSize = Math.max(1, Number(opts.chunkSize || 10)|0);

      // découpe en chunks → 1 qz.print() par chunk, items **as-is**
      var jobs = [];
      for (var i=0;i<etiquettes.length;i+=chunkSize){
        var slice = etiquettes.slice(i, i+chunkSize)
          .map(function(b64){
            // **AS-IS** : on n'édite RIEN
            if (protocole == 'MUNBYN') {
              return {
                type: 'pixel',
                format: 'pdf',
                flavor: 'base64',
                data: b64,
                options: {
                  colorType: 'grayscale',
                  scaleContent: false
                }
              };
            } else {
              return { type:'raw', format:'base64', data: b64 };
            }
          });
        jobs.push(slice);
      }

      DEBUG('chunks =', jobs.length, '| chunkSize =', chunkSize);

      // Enchaîne les jobs de façon séquentielle
      var seq = Promise.resolve();
      jobs.forEach(function(items, idx){
        seq = seq.then(function(){
          INFO('[SEND CHUNK]', (idx+1)+'/'+jobs.length, 'items=', items.length);
          return qz.print(cfg, items);
        });
      });

      return seq;
    })
    .catch(function(e){
      ERROR('printLabels failed', e);
      throw e;
    });
}

// Compat: garder ton nom d’API existant
export function printPayload(anyPayload, options){
  if (typeof anyPayload === 'string'){
    return printLabels({ url: anyPayload }, options);
  }
  if (anyPayload && (anyPayload.protocole || anyPayload.etiquettes || anyPayload.url)){
    return printLabels(anyPayload, options);
  }
  var url = anyPayload && (anyPayload.printer_request || anyPayload.request_url || anyPayload.url);
  if (url) return printLabels({ url: url }, options);

  throw new Error('printPayload: format de payload non reconnu');
}
